import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../app/http-common';
import NewspaperOutlinedIcon from '@mui/icons-material/NewspaperOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import './PublicSuggestionsPage.css';
import { toIso2 } from '../utils/languages';

function timeAgo(inputDate) {
  const now = new Date();
  const past = new Date(inputDate);
  const diffMs = now - past;
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  return days === 1 ? '1 day ago' : `${days} days ago`;
}

/* ────────── Live bucket-based page (legacy /b/:bucketId/suggestions) ────────── */

export default function PublicSuggestionsPage() {
  const { bucketId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getPublicSuggestions(bucketId);
      if (!res?.bucket?.name || !Array.isArray(res?.suggestions)) {
        throw new Error('Invalid response');
      }
      setData(res);
    } catch (err) {
      if (err?.response?.status === 404) {
        setError('We could not find suggestions for this link. Please check it with your contact at Alchemiq.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load suggestions');
      }
    } finally {
      setLoading(false);
    }
  }, [bucketId]);

  useEffect(() => { load(); }, [load]);

  const listRef = useRef(null);
  const scrollKey = `psg-scroll-bucket-${bucketId}`;

  const handleListScroll = useCallback(() => {
    if (listRef.current) {
      sessionStorage.setItem(scrollKey, String(listRef.current.scrollTop));
    }
  }, [scrollKey]);

  useEffect(() => {
    if (!loading && data && listRef.current) {
      const saved = sessionStorage.getItem(scrollKey);
      if (saved) {
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = parseInt(saved, 10);
        });
      }
    }
  }, [loading, data, scrollKey]);

  if (loading) return (
    <div className="psg-page">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">
        <div className="psg-loading">
          <div className="psg-spinner" />
          <span>Loading suggestions…</span>
        </div>
      </main>
    </div>
  );
  if (error) return (
    <div className="psg-page">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">
        <div className="psg-error">{error}</div>
      </main>
    </div>
  );
  if (!data) return null;

  const { bucket, suggestions } = data;
  const t = getTranslations(bucket?.targetLanguage, bucket?.origin);
  const isRtl = isRtlLanguage(bucket?.targetLanguage);
  const pageLang = getLangCode(bucket?.targetLanguage);

  return (
    <div className="psg-page" dir={isRtl ? 'rtl' : 'ltr'} lang={pageLang}>
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>

      <main className="psg-main">
        <div className="psg-card-surface">
          <div className="psg-header">
            <h1 className="psg-title">{bucket.name}</h1>
            <div className="psg-meta-row">
              <NewspaperOutlinedIcon className="psg-meta-icon" />
              <span>{t.storySuggestions}</span>
              <span className="psg-dot">·</span>
              <span className="psg-meta-badge">{suggestions.length} {suggestions.length === 1 ? t.article : t.articles}</span>
            </div>
          </div>

          {suggestions.length === 0 ? (
            <div className="psg-empty">
              <InboxOutlinedIcon className="psg-empty-icon" />
              <div>{t.empty}</div>
            </div>
          ) : (
            <div className="psg-list" ref={listRef} onScroll={handleListScroll}>
              {suggestions.map((s, idx) => {
                const feedbackClass = ['accepted', 'rejected'].includes(s.clientFeedbackStatus) ? `psg-item--${s.clientFeedbackStatus}` : '';
                return (
                  <div key={s._id} className={`psg-item ${feedbackClass}`}>
                    <div className="psg-item-index">{s.suggestionNumber || idx + 1}</div>
                    <div className="psg-item-content">
                      <div className="psg-item-header">
                        <h3 className="psg-item-title">{s.storyTitle}</h3>
                        {s.clientFeedbackStatus && (
                          <span className={`psg-status-badge psg-status-badge--${s.clientFeedbackStatus}`}>
                            {s.clientFeedbackStatus === 'accepted'
                              ? <><CheckCircleOutlineIcon className="psg-status-icon" /> {t.accepted}</>
                              : <><CancelOutlinedIcon className="psg-status-icon" /> {t.rejected}</>}
                          </span>
                        )}
                      </div>
                      <div className="psg-item-meta">
                        <NewspaperOutlinedIcon className="psg-meta-icon" />
                        <span>{s.articleCount} {s.articleCount !== 1 ? t.articles : t.article}</span>
                        <span className="psg-dot">·</span>
                        <AccessTimeIcon className="psg-meta-icon" />
                        <span>{timeAgo(s.addedAt)}</span>
                      </div>
                      {s.clientFeedbackComment && (
                        <div className="psg-feedback-comment">— {s.clientFeedbackComment}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ────────── Snapshot-based interactive page (/suggestions/:publicToken) ────────── */

const TRANSLATIONS = {
  en: {
    loading: 'Loading suggestions…',
    storySuggestions: 'Story proposals',
    shared: 'Shared',
    closedNotice: 'This suggestion list is no longer accepting responses.',
    empty: 'No suggestions in this list.',
    accept: 'Approve',
    reject: 'Reject',
    confirmReject: 'Confirm Reject',
    cancel: 'Cancel',
    accepted: 'Approved',
    rejected: 'Rejected',
    commentPlaceholder: 'Optional note…',
    saveNote: 'Save note',
    articles: 'articles',
    article: 'article',
    poweredBy: 'Powered by',
    productionChoice: 'Who writes this?',
    productionPublisher: 'We will write the story',
    productionAlchemiq: 'Please send us a written story',
    viewStory: 'Review',
    changeChoice: 'Change',
    workflowLockedWarning: 'This item is already in production. Your choice will be recorded but will not pull it back from the editor — please contact us if you need to stop the work.',
  },
  de: {
    loading: 'Vorschläge werden geladen…',
    storySuggestions: 'Story-Vorschläge',
    shared: 'Geteilt',
    closedNotice: 'Diese Vorschlagsliste nimmt keine Antworten mehr entgegen.',
    empty: 'Keine Vorschläge in dieser Liste.',
    accept: 'Annehmen',
    reject: 'Ablehnen',
    confirmReject: 'Ablehnung bestätigen',
    cancel: 'Abbrechen',
    accepted: 'Angenommen',
    rejected: 'Abgelehnt',
    commentPlaceholder: 'Optionaler Kommentar…',
    saveNote: 'Notiz speichern',
    articles: 'Artikel',
    article: 'Artikel',
    poweredBy: 'Bereitgestellt von',
    productionChoice: 'Wer schreibt das?',
    productionPublisher: 'Wir schreiben die Story selbst',
    productionAlchemiq: 'Bitte senden Sie uns eine geschriebene Story',
    viewStory: 'Ansehen',
    changeChoice: 'Ändern',
    workflowLockedWarning: 'Dieser Beitrag ist bereits in Produktion. Ihre Auswahl wird festgehalten, stoppt aber die laufende Arbeit nicht — bitte kontaktieren Sie uns, falls die Bearbeitung abgebrochen werden soll.',
  },
  he: {
    loading: 'טוען הצעות…',
    storySuggestions: 'הצעות לאייטמים',
    shared: 'שותף',
    closedNotice: 'רשימת ההצעות הזו כבר לא מקבלת תגובות.',
    empty: 'אין הצעות ברשימה זו.',
    accept: 'אישור',
    reject: 'דחייה',
    confirmReject: 'אישור דחייה',
    cancel: 'ביטול',
    accepted: 'אושר',
    rejected: 'נדחה',
    commentPlaceholder: 'הערה אופציונלית…',
    saveNote: 'שמירת הערה',
    articles: 'כתבות',
    article: 'כתבה',
    poweredBy: 'מופעל על ידי',
    productionChoice: 'מי כותב את זה?',
    productionPublisher: 'אנחנו נכתוב את הסיפור',
    productionAlchemiq: 'אנא שלחו לנו סיפור כתוב',
    viewStory: 'צפייה',
    changeChoice: 'שינוי',
    workflowLockedWarning: 'הפריט הזה כבר נמצא בעבודה. הבחירה שלך תישמר אך לא תעצור את העבודה שבביצוע — צרו איתנו קשר אם יש צורך לעצור.',
  },
};

// From the shared registry rather than a local 10-language table. `lang` may
// arrive as a display name (bucket.targetLanguage), an ISO 639-1 code
// (bucket.contactPointLanguage) or an ISO 639-2 code, so it must accept all
// three; the page's translations are keyed by ISO 639-1.
export function getLangCode(lang) {
  if (!lang) return 'en';
  return toIso2(lang) || String(lang).toLowerCase().split('-')[0].trim();
}

/**
 * §11 — a self-service recipient is a prospective client judging RELEVANCE, not
 * an editor approving work into a production queue. Only the wording changes:
 * the submitted values, the snapshot and the optimization they feed are
 * identical, so one vocabulary swap covers the whole requirement.
 */
const SELF_SERVICE_LABELS = {
  en: {
    accept: 'Relevant',
    reject: 'Not relevant',
    accepted: 'Relevant',
    rejected: 'Not relevant',
    confirmReject: 'Confirm',
    commentPlaceholder: 'Tell us why — optional',
  },
  de: {
    accept: 'Relevant',
    reject: 'Nicht relevant',
    accepted: 'Relevant',
    rejected: 'Nicht relevant',
    confirmReject: 'Bestätigen',
    commentPlaceholder: 'Sagen Sie uns warum — optional',
  },
  he: {
    accept: 'רלוונטי',
    reject: 'לא רלוונטי',
    accepted: 'רלוונטי',
    rejected: 'לא רלוונטי',
    confirmReject: 'אישור',
    commentPlaceholder: 'ספרו לנו למה — אופציונלי',
  },
};

export function getTranslations(lang, origin) {
  const key = lang ? getLangCode(lang) : 'en';
  const base = TRANSLATIONS[key] || TRANSLATIONS.en;
  if (origin !== 'self_service_builder') return base;
  // Fall back to the English relabelling for a language we have no self-service
  // wording for — a mixed-language page is better than showing "Approve" to
  // someone who never approved anything.
  return { ...base, ...(SELF_SERVICE_LABELS[key] || SELF_SERVICE_LABELS.en) };
}

export function isRtlLanguage(lang) {
  if (!lang) return false;
  const code = getLangCode(lang);
  return code === 'he' || code === 'ar';
}

export function PublicSnapshotPage() {
  const { publicToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [commentVisible, setCommentVisible] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [productionPending, setProductionPending] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getPublicSuggestionList(publicToken);
      if (!res?.list || !Array.isArray(res?.items)) throw new Error('Invalid response');
      setData(res);
    } catch (err) {
      if (err?.response?.status === 404) {
        // Suggestion lists are never deleted or expired, so a token we cannot find
        // was almost certainly damaged in transit — mail clients wrap long URLs and
        // only part of the link survives the copy. Say that, because "not found"
        // reads like the list is gone when it is sitting there intact.
        setError(
          'We could not find this suggestion list. The link may have been cut short when it '
          + 'was sent — please open it directly from the original message, or ask your contact to resend it.'
        );
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load suggestion list');
      }
    } finally {
      setLoading(false);
    }
  }, [publicToken]);

  useEffect(() => { load(); }, [load]);

  const listRef = useRef(null);
  const scrollKey = `psg-scroll-snapshot-${publicToken}`;

  const handleListScroll = useCallback(() => {
    if (listRef.current) {
      sessionStorage.setItem(scrollKey, String(listRef.current.scrollTop));
    }
  }, [scrollKey]);

  useEffect(() => {
    if (!loading && data && listRef.current) {
      const saved = sessionStorage.getItem(scrollKey);
      if (saved) {
        requestAnimationFrame(() => {
          if (listRef.current) listRef.current.scrollTop = parseInt(saved, 10);
        });
      }
    }
  }, [loading, data, scrollKey]);

  const handleResponse = async (itemId, responseStatus, productionType) => {
    const comment = commentInputs[itemId] || '';
    setSubmitting(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.submitSnapshotResponse(publicToken, itemId, responseStatus, comment, productionType || undefined);
      const res = await api.getPublicSuggestionList(publicToken);
      if (res?.list && Array.isArray(res?.items)) setData(res);
      setProductionPending(null);
      setCommentInputs(prev => { const n = { ...prev }; delete n[itemId]; return n; });
      setCommentVisible(prev => { const n = { ...prev }; delete n[itemId]; return n; });
    } catch (err) {
      setSubmitError(err.response?.data?.error || 'Failed to submit response');
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setSubmitting(prev => ({ ...prev, [itemId]: false }));
    }
  };

  const handleAcceptClick = (itemId, isAlreadyAccepted) => {
    if (isAlreadyAccepted) return;
    const item = data?.items?.find(i => i._id === itemId);
    // On producesOwnStories buckets we need a productionType for every accept
    // transition. Skip the prompt only if the bucket story already carries one
    // (preserve last choice on re-accept — Q3); otherwise ask, even on toggle.
    if (data?.bucket?.producesOwnStories && !item?.bucketStoryProductionType) {
      setProductionPending(itemId);
    } else {
      handleResponse(itemId, 'accepted');
    }
  };

  const toggleComment = (itemId) => {
    setCommentVisible(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // A note typed AFTER the accept/reject decision had nowhere to go: the
  // decision was already persisted, both buttons were disabled, and the text
  // was dropped on the next render. People naturally decide first and explain
  // second, so the "tell us why" the optimization loop depends on was being
  // thrown away. Re-submitting the SAME status with a comment is an explicit
  // no-op-unless-commented path on the server, so this saves the note without
  // changing the decision.
  const saveComment = (itemId, currentStatus) => {
    if (!currentStatus) return;
    if (!(commentInputs[itemId] || '').trim()) return;
    handleResponse(itemId, currentStatus);
  };

  if (loading) return (
    <div className="psg-page">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">
        <div className="psg-loading">
          <div className="psg-spinner" />
          <span>Loading suggestions…</span>
        </div>
      </main>
    </div>
  );
  if (error) return (
    <div className="psg-page">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">
        <div className="psg-error">{error}</div>
      </main>
    </div>
  );
  if (!data) return null;

  const { list, bucket, items } = data;
  // PRD item 10 — an inactive bucket's already-emailed link keeps rendering.
  // The list is a frozen record of something we really did send, so 404ing it
  // would read as our outage; but nobody is minding the bucket, so no NEW
  // response may land. The server decides (it also still answers
  // `status === 'shared'`), and the existing closed-list presentation —
  // `closedNotice` plus prior verdicts as read-only badges — says so in the
  // client's own language with no new copy. The fallback keeps a cached bundle
  // working against the new API.
  const isAcceptingResponses = list.acceptingResponses !== undefined
    ? list.acceptingResponses
    : list.status === 'shared';
  const effectiveLang = bucket?.contactPointLanguage || bucket?.targetLanguage || list?.language;
  const t = getTranslations(effectiveLang, bucket?.origin);
  const isRtl = isRtlLanguage(effectiveLang);
  const pageLang = getLangCode(effectiveLang);

  return (
    <div className="psg-page" dir={isRtl ? 'rtl' : 'ltr'} lang={pageLang}>
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>

      <main className="psg-main">
        <div className="psg-card-surface">
          <div className="psg-header">
            <h1 className="psg-title">{bucket?.name || 'Suggestions'}</h1>
            <div className="psg-meta-row">
              <NewspaperOutlinedIcon className="psg-meta-icon" />
              <span>{t.storySuggestions}</span>
              <span className="psg-dot">·</span>
              <span className="psg-meta-badge">{items.length} {items.length === 1 ? t.article : t.articles}</span>
              {list.sharedAt && (
                <>
                  <span className="psg-dot">·</span>
                  <AccessTimeIcon className="psg-meta-icon" />
                  <span>{t.shared} {timeAgo(list.sharedAt)}</span>
                </>
              )}
            </div>
          </div>

          {!isAcceptingResponses && (
            <div className="psg-notice">
              <InfoOutlinedIcon className="psg-notice-icon" />
              {t.closedNotice}
            </div>
          )}

          {submitError && (
            <div className="psg-notice psg-notice--error">
              <InfoOutlinedIcon className="psg-notice-icon" />
              {submitError}
            </div>
          )}

          {items.length === 0 ? (
            <div className="psg-empty">
              <InboxOutlinedIcon className="psg-empty-icon" />
              <div>{t.empty}</div>
            </div>
          ) : (
            <div className="psg-list" ref={listRef} onScroll={handleListScroll}>
              {items.map((item, idx) => {
                const hasResponse = !!item.clientResponseStatus;
                const currentStatus = item.clientResponseStatus;
                const statusClass = hasResponse ? `psg-item--${currentStatus}` : '';
                const isSubmitting = submitting[item._id];
                const isProductionPending = productionPending === item._id;
                const showComment = commentVisible[item._id];
                // Workflow lock: once admin moved past suggestion/accepted/rejected,
                // toggling still records the editor's preference but won't pull
                // the story back from in-flight production work.
                const reversibleStatuses = ['suggestion', 'accepted', 'rejected', null, undefined];
                // An accept on a story we went on to produce is not a conflict: the
                // warning used to tell the client their own choice "won't stop the
                // work" on the very item they had just chosen.
                const producedAfterAccept = currentStatus === 'accepted'
                  && ['in_work', 'completed', 'sent_to_publisher', 'published'].includes(item.bucketStoryStatus);
                const workflowLocked = hasResponse && !producedAfterAccept
                  && !reversibleStatuses.includes(item.bucketStoryStatus);

                return (
                  <div key={item._id} className={`psg-item ${statusClass}`}>
                    <div className="psg-item-index">{item.suggestionNumber}</div>
                    <div className="psg-item-content">
                      <div className="psg-item-header">
                        <h3 className="psg-item-title">
                          {item.storyUrlSnapshot ? (
                            <a href={item.storyUrlSnapshot} target="_blank" rel="noopener noreferrer" className="psg-item-link">
                              {item.titleSnapshot}
                            </a>
                          ) : item.titleSnapshot}
                        </h3>
                        {isAcceptingResponses && !isProductionPending && (
                          <div className="psg-action-row" role="group" aria-label={t.storySuggestions}>
                            <button
                              className="psg-btn psg-btn--comment"
                              onClick={() => toggleComment(item._id)}
                              disabled={isSubmitting}
                              title={t.commentPlaceholder}
                              aria-label={t.commentPlaceholder}
                            >
                              <ChatBubbleOutlineIcon className="psg-btn-icon" />
                            </button>
                            <button
                              className={`psg-btn psg-btn--reject ${currentStatus === 'rejected' ? 'psg-btn--selected' : ''}`}
                              onClick={() => handleResponse(item._id, 'rejected')}
                              disabled={isSubmitting || currentStatus === 'rejected'}
                              aria-pressed={currentStatus === 'rejected'}
                            >
                              <CancelOutlinedIcon className="psg-btn-icon" />
                              {t.reject}
                            </button>
                            <button
                              className={`psg-btn psg-btn--accept ${currentStatus === 'accepted' ? 'psg-btn--selected' : ''}`}
                              onClick={() => handleAcceptClick(item._id, currentStatus === 'accepted')}
                              disabled={isSubmitting || currentStatus === 'accepted'}
                              aria-pressed={currentStatus === 'accepted'}
                            >
                              <CheckCircleOutlineIcon className="psg-btn-icon" />
                              {t.accept}
                            </button>
                          </div>
                        )}
                        {!isAcceptingResponses && hasResponse && (
                          <span className={`psg-status-badge psg-status-badge--${currentStatus}`}>
                            {currentStatus === 'accepted'
                              ? <><CheckCircleOutlineIcon className="psg-status-icon" /> {t.accepted}</>
                              : <><CancelOutlinedIcon className="psg-status-icon" /> {t.rejected}</>}
                          </span>
                        )}
                      </div>

                      {item.clientResponseComment && (
                        <div className="psg-feedback-comment">— {item.clientResponseComment}</div>
                      )}

                      {workflowLocked && isAcceptingResponses && (
                        <div className="psg-item-warning">
                          <InfoOutlinedIcon className="psg-notice-icon" />
                          <span>{t.workflowLockedWarning}</span>
                        </div>
                      )}

                      {showComment && isAcceptingResponses && (
                        <div className="psg-comment-row">
                          <input
                            type="text"
                            className="psg-comment-input"
                            placeholder={t.commentPlaceholder}
                            value={commentInputs[item._id] || ''}
                            onChange={(e) => setCommentInputs(prev => ({ ...prev, [item._id]: e.target.value }))}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveComment(item._id, currentStatus);
                              }
                            }}
                          />
                          {currentStatus && (
                            <button
                              type="button"
                              className="psg-btn psg-btn--save-comment"
                              onClick={() => saveComment(item._id, currentStatus)}
                              disabled={isSubmitting || !(commentInputs[item._id] || '').trim()}
                            >
                              {t.saveNote}
                            </button>
                          )}
                        </div>
                      )}

                      {isProductionPending && (
                        <div className="psg-production-choice">
                          <p className="psg-production-label">
                            <EditNoteOutlinedIcon className="psg-production-icon" />
                            {t.productionChoice}
                          </p>
                          <div className="psg-production-buttons">
                            <button
                              className="psg-btn psg-btn--accept"
                              onClick={() => handleResponse(item._id, 'accepted', 'publisher')}
                              disabled={isSubmitting}
                            >
                              {t.productionPublisher}
                            </button>
                            <button
                              className="psg-btn psg-btn--accept"
                              onClick={() => handleResponse(item._id, 'accepted', 'alchemiq')}
                              disabled={isSubmitting}
                            >
                              {t.productionAlchemiq}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

/* ───────────── Public shared artifact (/shared/:publicToken) ─────────────
 *
 * PRD §C3 — the page "Send to client" links to. This exists because the send
 * path minted a token and mailed `${origin}/shared/<token>` while nothing
 * served that URL: every artifact we sent resolved to a 404.
 *
 * It renders the FROZEN snapshot the artifact captured at send time, never the
 * live draft — that is the whole point of an immutable client-facing version
 * (see backend/briefs/sendToClientService.js). The endpoint returns an
 * allow-listed projection, so there is nothing here to leak about the bucket,
 * the client or the other recipients.
 */
export function PublicSharedArtifactPage() {
  const { publicToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicSharedArtifact(publicToken);
        if (!cancelled) setData(res.data || res);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'This link is no longer available.'
            : 'Could not load this page.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicToken]);

  const shell = (inner, dir = 'ltr', lang = 'en') => (
    <div className="psg-page" dir={dir} lang={lang}>
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">{inner}</main>
    </div>
  );

  if (loading) return shell(<div className="psg-loading"><div className="psg-spinner" /></div>);
  if (error) return shell(<div className="psg-error">{error}</div>);
  if (!data) return null;

  const isRtl = isRtlLanguage(data.language);
  const pageLang = getLangCode(data.language);

  return shell(
    <div className="psg-card-surface">
      <div className="psg-header">
        <h1 className="psg-title">{data.title || 'Untitled'}</h1>
        {data.sentAt && (
          <div className="psg-meta-row">
            <AccessTimeIcon className="psg-meta-icon" style={{ fontSize: 14 }} />
            <span>{timeAgo(data.sentAt)}</span>
          </div>
        )}
      </div>
      {/* The snapshot is produced by our own generation pipeline, not by a
          client, so the HTML is ours. Plain text is the fallback when an older
          artifact captured no HTML. */}
      {data.html
        ? <div className="psg-artifact-body" dangerouslySetInnerHTML={{ __html: data.html }} />
        : <div className="psg-artifact-body psg-artifact-body--plain">{data.text}</div>}
    </div>,
    isRtl ? 'rtl' : 'ltr',
    pageLang,
  );
}

/* ───────────── Public Signal page (/signals/:publicToken) ─────────────
 *
 * PRD §D7. The Signal alert mails this link, and the backend endpoint existed
 * with a carefully allow-listed projection — but no page rendered it, so the
 * link 404'd exactly as the shared-artifact link did.
 *
 * Everything shown here comes from that allow-list: the story, its own observed
 * velocity evidence, and the brief. There is deliberately no routing, bucket,
 * client or score information to render, because §5 forbids a public Signal
 * page revealing cross-client or internal-bucket detail.
 */
export function PublicSignalPage() {
  const { publicToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicSignal(publicToken);
        if (!cancelled) setData(res.data || res);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'This signal is no longer available.'
            : 'Could not load this signal.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [publicToken]);

  const shell = (inner) => (
    <div className="psg-page" dir="ltr" lang="en">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">{inner}</main>
    </div>
  );

  if (loading) return shell(<div className="psg-loading"><div className="psg-spinner" /></div>);
  if (error) return shell(<div className="psg-error">{error}</div>);
  if (!data) return null;

  const e = data.evidence || {};
  return shell(
    <div className="psg-card-surface">
      <div className="psg-header">
        <h1 className="psg-title">{data.title || 'Signal'}</h1>
        <div className="psg-meta-row">
          {data.detectedAt && (
            <>
              <AccessTimeIcon className="psg-meta-icon" style={{ fontSize: 14 }} />
              <span>detected {timeAgo(data.detectedAt)}</span>
            </>
          )}
          {(data.categories || []).map((c) => (
            <span key={c} className="psg-meta-badge">{c}</span>
          ))}
        </div>
      </div>
      <div className="psg-artifact-body">
        {/* Observations presented as observations (§2). No score, no threshold,
            no indication of which buckets or clients this reached. */}
        <div className="psg-signal-evidence">
          <div><strong>{e.itemsInWindow ?? 0}</strong> items in <strong>{e.windowMinutes ?? 0}</strong> minutes</div>
          <div><strong>{e.distinctPublishers ?? 0}</strong> distinct publishers</div>
          <div>story age <strong>{e.storyAgeMinutes ?? 0}</strong> minutes</div>
        </div>
        {data.brief
          ? <div className="psg-artifact-body--plain">{data.brief}</div>
          : <p>No brief is available for this signal.</p>}
      </div>
    </div>,
  );
}

/* ────────── Signal alert story view (/signal-story/:token) ────────── */

/**
 * The page a Signal alert email links to. No login. Shows the story, its
 * growth and its source articles with their original links. It never names a
 * client or a bucket and never shows the brief: the brief went to the
 * recipients in the email itself.
 */
export function PublicSignalStoryPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getPublicSignalStory(token);
        if (!cancelled) setData(res.data || res);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.status === 404
            ? 'This story is no longer available.'
            : 'Could not load this story.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const shell = (inner) => (
    <div className="psg-page" dir="ltr" lang="en">
      <div className="psg-top-bar">
        <div className="psg-top-bar-inner">
          <a href="/" className="psg-logo-link">
            <img src="/textOnlyLogoOrr-01.png" alt="Alchemiq" className="psg-logo" onError={(e) => { e.target.style.display = 'none'; }} />
          </a>
        </div>
      </div>
      <main className="psg-main">{inner}</main>
    </div>
  );

  if (loading) return shell(<div className="psg-loading"><div className="psg-spinner" /></div>);
  if (error) return shell(<div className="psg-error">{error}</div>);
  if (!data) return null;

  const articles = data.articles || [];
  return shell(
    <div className="psg-card-surface">
      <div className="psg-header">
        <h1 className="psg-title">{data.title || 'Story'}</h1>
        <div className="psg-meta-row">
          {data.sentAt && (
            <>
              <AccessTimeIcon className="psg-meta-icon" style={{ fontSize: 14 }} />
              <span>sent {timeAgo(data.sentAt)}</span>
            </>
          )}
          <span className="psg-meta-badge">{articles.length} source article{articles.length === 1 ? '' : 's'}</span>
        </div>
      </div>
      {data.growth && (
        <div className="psg-signal-evidence">
          <div>Growth: <strong>{data.growth.window}</strong></div>
        </div>
      )}
      <div className="psg-list">
        {articles.map((a, i) => (
          <div key={`${a.url || a.headline}-${i}`} className="psg-item">
            <div className="psg-item-index">{i + 1}</div>
            <div className="psg-item-content">
              <h3 className="psg-item-title" dir="auto">
                {a.url
                  ? <a className="psg-item-link" href={a.url} target="_blank" rel="noopener noreferrer">{a.headline || a.url}</a>
                  : a.headline}
              </h3>
              <div className="psg-item-meta">
                {a.publisher && <span>{a.publisher}</span>}
                {a.publishedAt && (
                  <>
                    <AccessTimeIcon className="psg-meta-icon" style={{ fontSize: 14 }} />
                    <span>{timeAgo(a.publishedAt)}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>,
  );
}
